import { isDevMode } from '@angular/core';
import moment from 'moment-timezone';
import { dayNumber, formatLongDay, formatTimeDisplay, outOfBounds, SB_LOCALE } from './sb-date-time-format';
import { SbDateParts, SbTimeParts } from './sb-date-time-parse';

/**
 * One-click relative picks for the date, time and datetime fields, and presets for the range.
 * Every pick is computed in the field's zone from an injected clock:
 * - calendar adds (days, weeks, months, years) keep the wall clock, and month and year adds clamp
 *   to the month's last day (31 Jan + 1 month = 28/29 Feb);
 * - minute and hour adds are elapsed time, so they cross a DST change by the real hour;
 * - no rounding: "In 1 hour" at 10:37 is 11:37;
 * - a wall time in a spring-forward gap moves forward by the gap, and a repeated one (fall back)
 *   takes the first occurrence, as moment-timezone does; an hour add that lands in the second
 *   occurrence is unavailable, because the field holds a wall time and would save the first
 *   ("In 2 hours" would save what "In 1 hour" does), unless it is a time with no date joined to it,
 *   which has no offset and is not ambiguous;
 * - weeks start on Sunday, the panel's fixed week start.
 * A list keeps one shape all day: a pick that does not apply now stays, disabled with its reason.
 */
export type SbQuickPickId =
  | 'today' | 'tomorrow' | 'in3Days' | 'friday' | 'nextMonday' | 'in1Week' | 'in2Weeks'
  | 'endOfMonth' | 'startOfNextMonth' | 'in1Month' | 'in3Months' | 'in6Months' | 'in1Year' | 'latest'
  | 'yesterday' | 'days7Ago' | 'days30Ago' | 'startOfWeek' | 'startOfMonth' | 'startOfLastMonth'
  | 'months3Ago' | 'months6Ago' | 'year1Ago'
  | 'now' | 'in15Minutes' | 'in30Minutes' | 'in1Hour' | 'in2Hours' | 'in3Hours' | 'in4Hours' | 'in24Hours'
  | 'at9' | 'at12' | 'at17' | 'minutes15Ago' | 'hour1Ago'
  | 'todayAt18' | 'tomorrowThisTime' | 'tomorrowAt9' | 'nextMondayAt9' | 'in1WeekThisTime' | 'in1MonthThisTime'
  | 'todayAt9' | 'yesterdayThisTime' | 'yesterdayAt9';

export type SbRangePresetId =
  | 'today' | 'yesterday' | 'last7Days' | 'last14Days' | 'last30Days' | 'last90Days'
  | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter'
  | 'yearToDate' | 'last12Months' | 'allTime'
  | 'next7Days' | 'next30Days' | 'nextMonth' | 'nextQuarter';

export type SbPickMode = 'date' | 'time' | 'datetime';

/** `'future'` (scheduling, end dates, expiry), `'past'` (filters, reports, incident times), none, or a list. */
export type SbQuickPickSet = 'future' | 'past' | 'none' | readonly SbQuickPickId[];
export type SbRangePresetSet = 'future' | 'past' | 'none' | readonly SbRangePresetId[];

/** What a pick sets: a day, a time on a day, or a range (null bounds for All time). */
export interface SbPickResult {
  date: SbDateParts | null;
  time: SbTimeParts | null;
  end?: SbDateParts | null;
}

/** A pick as the panel shows it: "{label}: {value}" ("Tomorrow: Thu, Oct 29"). */
export interface SbPickView {
  id: string;
  label: string;
  /** The value it sets, as a preview; '' when the label says it ("9:00 AM"). */
  value: string;
  /** Why the pick cannot be used now ("after the latest allowed date, Nov 1, 2026"), or ''. */
  reason: string;
  result: SbPickResult | null;
}

/** The clock and bounds a list is computed with. */
export interface SbPickContext {
  /** The current instant; the panel passes `Date.now()`, specs a fixed one. */
  now: number;
  /** IANA zone; null is the browser's. */
  zone: string | null;
  min: SbDateParts | null;
  max: SbDateParts | null;
  /** A time field whose time the page joins to a date field: its wall time then has an offset. */
  joinsDate?: boolean;
}

type Now = moment.Moment;
/** `past` is true in a `'past'` list; `dated` when the field holds a date (not a time field). */
type Compute = (now: Now, today: Now, ctx: SbPickContext, past: boolean, dated: boolean) => Now | null;
type ComputeRange = (today: Now) => [Now | null, Now | null];

interface PickDef {
  label: string;
  /** Picks without a time of day (date picks, `at9` in a date field) have none. */
  timed: boolean;
  compute: Compute;
  /** The reason when `compute` gives null. */
  unavailable?: (ctx: SbPickContext, past: boolean) => string;
}

const at = (day: Now, hour: number, minute: number): Now =>
  moment.tz([day.year(), day.month(), day.date(), hour, minute], day.tz() || moment.tz.guess());

/** Now's wall clock on another day; built from parts so a time in a DST gap moves forward. */
const sameTime = (day: Now, now: Now): Now => at(day, now.hours(), now.minutes());

/**
 * A fixed time today. In a field that holds a date, a future list has it unavailable once it has
 * passed and a past list until it comes, so no list offers a time on the wrong side of now; `past`
 * fixes the side for a pick that only one kind of list has. A time field holds no date (the launch
 * dialog's date is a field of its own, maybe next week), so there it is always available. `name`
 * is the time as the reason says it.
 */
const fixedToday = (label: string, hour: number, name = label, past?: boolean): PickDef => ({
  label,
  timed: true,
  compute: (now, today, _ctx, inPast, dated) => {
    const time = at(today, hour, 0);
    return dated && (past ?? inPast) === now.isBefore(time) ? null : time;
  },
  unavailable: (_ctx, inPast) => ((past ?? inPast) ? `${name} is later today` : `${name} has passed`),
});

/** The coming weekday (0 = Sunday), 1 to 7 days on, never today. */
const coming = (today: Now, weekday: number): Now => today.clone().add((weekday - today.day() + 7) % 7 || 7, 'days');

const PICKS: Record<SbQuickPickId, PickDef> = {
  // Date, future
  today: { label: 'Today', timed: false, compute: (_now, today) => today },
  tomorrow: { label: 'Tomorrow', timed: false, compute: (_now, today) => today.clone().add(1, 'day') },
  in3Days: { label: 'In 3 days', timed: false, compute: (_now, today) => today.clone().add(3, 'days') },
  friday: { label: 'Friday', timed: false, compute: (_now, today) => coming(today, 5) },
  nextMonday: { label: 'Next Monday', timed: false, compute: (_now, today) => coming(today, 1) },
  in1Week: { label: 'In 1 week', timed: false, compute: (_now, today) => today.clone().add(1, 'week') },
  in2Weeks: { label: 'In 2 weeks', timed: false, compute: (_now, today) => today.clone().add(2, 'weeks') },
  endOfMonth: { label: 'End of this month', timed: false, compute: (_now, today) => today.clone().endOf('month').startOf('day') },
  startOfNextMonth: { label: 'Start of next month', timed: false, compute: (_now, today) => today.clone().add(1, 'month').startOf('month') },
  in1Month: { label: 'In 1 month', timed: false, compute: (_now, today) => today.clone().add(1, 'month') },
  in3Months: { label: 'In 3 months', timed: false, compute: (_now, today) => today.clone().add(3, 'months') },
  in6Months: { label: 'In 6 months', timed: false, compute: (_now, today) => today.clone().add(6, 'months') },
  in1Year: { label: 'In 1 year', timed: false, compute: (_now, today) => today.clone().add(1, 'year') },
  latest: {
    label: 'Latest allowed',
    timed: false,
    compute: (_now, today, ctx) => (ctx.max ? at(today, 0, 0).set({ year: ctx.max.year, month: ctx.max.month, date: ctx.max.day }) : null),
    unavailable: () => 'no latest date is set',
  },
  // Date, past
  yesterday: { label: 'Yesterday', timed: false, compute: (_now, today) => today.clone().subtract(1, 'day') },
  days7Ago: { label: '7 days ago', timed: false, compute: (_now, today) => today.clone().subtract(7, 'days') },
  days30Ago: { label: '30 days ago', timed: false, compute: (_now, today) => today.clone().subtract(30, 'days') },
  startOfWeek: { label: 'Start of this week', timed: false, compute: (_now, today) => today.clone().subtract(today.day(), 'days') },
  startOfMonth: { label: 'Start of this month', timed: false, compute: (_now, today) => today.clone().startOf('month') },
  startOfLastMonth: { label: 'Start of last month', timed: false, compute: (_now, today) => today.clone().subtract(1, 'month').startOf('month') },
  months3Ago: { label: '3 months ago', timed: false, compute: (_now, today) => today.clone().subtract(3, 'months') },
  months6Ago: { label: '6 months ago', timed: false, compute: (_now, today) => today.clone().subtract(6, 'months') },
  year1Ago: { label: '1 year ago', timed: false, compute: (_now, today) => today.clone().subtract(1, 'year') },
  // Time and datetime, soon
  now: { label: 'Now', timed: true, compute: (now) => now },
  in15Minutes: { label: 'In 15 minutes', timed: true, compute: (now) => now.clone().add(15, 'minutes') },
  in30Minutes: { label: 'In 30 minutes', timed: true, compute: (now) => now.clone().add(30, 'minutes') },
  in1Hour: { label: 'In 1 hour', timed: true, compute: (now) => now.clone().add(1, 'hour') },
  in2Hours: { label: 'In 2 hours', timed: true, compute: (now) => now.clone().add(2, 'hours') },
  in3Hours: { label: 'In 3 hours', timed: true, compute: (now) => now.clone().add(3, 'hours') },
  in4Hours: { label: 'In 4 hours', timed: true, compute: (now) => now.clone().add(4, 'hours') },
  in24Hours: { label: 'In 24 hours', timed: true, compute: (now) => now.clone().add(24, 'hours') },
  minutes15Ago: { label: '15 minutes ago', timed: true, compute: (now) => now.clone().subtract(15, 'minutes') },
  hour1Ago: { label: '1 hour ago', timed: true, compute: (now) => now.clone().subtract(1, 'hour') },
  // Times of day (today's)
  at9: fixedToday('9:00 AM', 9),
  at12: fixedToday('12:00 PM', 12),
  at17: fixedToday('5:00 PM', 17),
  // Datetime, later
  todayAt18: fixedToday('Today at 6:00 PM', 18, '6:00 PM', false),
  tomorrowThisTime: { label: 'Tomorrow at this time', timed: true, compute: (now, today) => sameTime(today.clone().add(1, 'day'), now) },
  tomorrowAt9: { label: 'Tomorrow at 9:00 AM', timed: true, compute: (_now, today) => at(today.clone().add(1, 'day'), 9, 0) },
  nextMondayAt9: { label: 'Next Monday at 9:00 AM', timed: true, compute: (_now, today) => at(coming(today, 1), 9, 0) },
  in1WeekThisTime: { label: 'In 1 week at this time', timed: true, compute: (now, today) => sameTime(today.clone().add(1, 'week'), now) },
  in1MonthThisTime: { label: 'In 1 month at this time', timed: true, compute: (now, today) => sameTime(today.clone().add(1, 'month'), now) },
  // Datetime, earlier
  todayAt9: fixedToday('Today at 9:00 AM', 9, '9:00 AM', true),
  yesterdayThisTime: { label: 'Yesterday at this time', timed: true, compute: (now, today) => sameTime(today.clone().subtract(1, 'day'), now) },
  yesterdayAt9: { label: 'Yesterday at 9:00 AM', timed: true, compute: (_now, today) => at(today.clone().subtract(1, 'day'), 9, 0) },
};

/** The sets each mode offers, in the order they are listed. */
export const QUICK_PICK_SETS: Record<SbPickMode, Record<'future' | 'past', readonly SbQuickPickId[]>> = {
  date: {
    future: ['today', 'tomorrow', 'in3Days', 'friday', 'nextMonday', 'in1Week', 'in2Weeks', 'endOfMonth', 'startOfNextMonth', 'in1Month', 'in3Months', 'in6Months', 'in1Year'],
    past: ['today', 'yesterday', 'days7Ago', 'startOfWeek', 'days30Ago', 'startOfMonth', 'startOfLastMonth', 'months3Ago', 'months6Ago', 'year1Ago'],
  },
  time: {
    future: ['now', 'in15Minutes', 'in30Minutes', 'in1Hour', 'in2Hours', 'in4Hours', 'at9', 'at12', 'at17'],
    past: ['now', 'minutes15Ago', 'hour1Ago', 'at9', 'at12'],
  },
  datetime: {
    future: ['now', 'in15Minutes', 'in1Hour', 'in3Hours', 'in24Hours', 'todayAt18', 'tomorrowThisTime', 'tomorrowAt9', 'nextMondayAt9', 'in1WeekThisTime', 'in1MonthThisTime'],
    past: ['now', 'minutes15Ago', 'hour1Ago', 'todayAt9', 'yesterdayThisTime', 'yesterdayAt9'],
  },
};

const RANGES: Record<SbRangePresetId, { label: string; compute: ComputeRange }> = {
  today: { label: 'Today', compute: (t) => [t, t] },
  yesterday: { label: 'Yesterday', compute: (t) => [t.clone().subtract(1, 'day'), t.clone().subtract(1, 'day')] },
  last7Days: { label: 'Last 7 days', compute: (t) => [t.clone().subtract(6, 'days'), t] },
  last14Days: { label: 'Last 14 days', compute: (t) => [t.clone().subtract(13, 'days'), t] },
  last30Days: { label: 'Last 30 days', compute: (t) => [t.clone().subtract(29, 'days'), t] },
  last90Days: { label: 'Last 90 days', compute: (t) => [t.clone().subtract(89, 'days'), t] },
  thisWeek: { label: 'This week', compute: (t) => [t.clone().subtract(t.day(), 'days'), t.clone().add(6 - t.day(), 'days')] },
  lastWeek: { label: 'Last week', compute: (t) => [t.clone().subtract(t.day() + 7, 'days'), t.clone().subtract(t.day() + 1, 'days')] },
  thisMonth: { label: 'This month', compute: (t) => [t.clone().startOf('month'), t.clone().endOf('month').startOf('day')] },
  lastMonth: { label: 'Last month', compute: (t) => [t.clone().subtract(1, 'month').startOf('month'), t.clone().subtract(1, 'month').endOf('month').startOf('day')] },
  thisQuarter: { label: 'This quarter', compute: (t) => [t.clone().startOf('quarter'), t.clone().endOf('quarter').startOf('day')] },
  lastQuarter: { label: 'Last quarter', compute: (t) => [t.clone().subtract(1, 'quarter').startOf('quarter'), t.clone().subtract(1, 'quarter').endOf('quarter').startOf('day')] },
  yearToDate: { label: 'Year to date', compute: (t) => [t.clone().startOf('year'), t] },
  last12Months: { label: 'Last 12 months', compute: (t) => [t.clone().subtract(1, 'year').add(1, 'day'), t] },
  allTime: { label: 'All time', compute: () => [null, null] },
  next7Days: { label: 'Next 7 days', compute: (t) => [t, t.clone().add(6, 'days')] },
  next30Days: { label: 'Next 30 days', compute: (t) => [t, t.clone().add(29, 'days')] },
  nextMonth: { label: 'Next month', compute: (t) => [t.clone().add(1, 'month').startOf('month'), t.clone().add(1, 'month').endOf('month').startOf('day')] },
  nextQuarter: { label: 'Next quarter', compute: (t) => [t.clone().add(1, 'quarter').startOf('quarter'), t.clone().add(1, 'quarter').endOf('quarter').startOf('day')] },
};

export const RANGE_PRESET_SETS: Record<'future' | 'past', readonly SbRangePresetId[]> = {
  past: ['today', 'yesterday', 'last7Days', 'last14Days', 'last30Days', 'last90Days', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'yearToDate', 'last12Months', 'allTime'],
  future: ['next7Days', 'next30Days', 'nextMonth', 'nextQuarter'],
};

const zoneNow = (ctx: SbPickContext): Now => {
  const now = ctx.zone ? moment.tz(ctx.now, ctx.zone) : moment.tz(ctx.now, moment.tz.guess());
  return now.seconds(0).milliseconds(0);
};

const toDay = (m: Now): SbDateParts => ({ year: m.year(), month: m.month(), day: m.date() });
const toTime = (m: Now): SbTimeParts => ({ hour: m.hours(), minute: m.minutes() });

const sameYear = (date: SbDateParts, today: SbDateParts) => date.year === today.year;

const dayLabel = (date: SbDateParts, today: SbDateParts, withWeekday = true): string =>
  new Date(date.year, date.month, date.day).toLocaleDateString(SB_LOCALE, {
    ...(withWeekday ? { weekday: 'short' as const } : {}),
    month: 'short',
    day: 'numeric',
    ...(sameYear(date, today) ? {} : { year: 'numeric' as const }),
  });

/** `Wed, Nov 4`; `9:15 AM tomorrow`; `Wed, Nov 4, 9:15 AM` (the year only when it is not this one). */
export const pickPreview = (mode: SbPickMode, result: SbPickResult, today: SbDateParts): string => {
  if (mode === 'date' || !result.time) {
    return result.date ? dayLabel(result.date, today) : '';
  }
  const time = formatTimeDisplay(result.time);
  if (mode === 'datetime') {
    return `${dayLabel(result.date as SbDateParts, today)}, ${time}`;
  }
  const day = result.date as SbDateParts;
  const offset = Math.round((Date.UTC(day.year, day.month, day.day) - Date.UTC(today.year, today.month, today.day)) / 86400000);
  if (offset === 1) {
    return `${time} tomorrow`;
  }
  if (offset === -1) {
    return `${time} yesterday`;
  }
  return offset ? `${time}, ${dayLabel(day, today)}` : time;
};

/** `Oct 22 – 28`, `Sep 29 – Oct 28`, `Oct 28`, `Jan 1 – Mar 31, 2027` (years only when not this one); `No date limits` for All time. */
export const rangePreview = (start: SbDateParts | null, end: SbDateParts | null, today: SbDateParts): string => {
  if (!start || !end) {
    return 'No date limits';
  }
  const thisYear = sameYear(start, today) && sameYear(end, today);
  if (dayNumber(start) === dayNumber(end)) {
    return dayLabel(start, today, false);
  }
  if (start.year === end.year && start.month === end.month) {
    return `${dayLabel(start, { ...today, year: start.year }, false)} – ${end.day}${thisYear ? '' : `, ${end.year}`}`;
  }
  if (start.year === end.year && !thisYear) {
    // One year, not this one: the year once, at the end (`Jan 1 – Mar 31, 2027`).
    const inYear = { ...today, year: start.year };
    return `${dayLabel(start, inYear, false)} – ${dayLabel(end, inYear, false)}, ${end.year}`;
  }
  const short = (date: SbDateParts) => dayLabel(date, thisYear ? today : { ...today, year: -1 }, false);
  return `${short(start)} – ${short(end)}`;
};

const boundReason = (side: 'min' | 'max', ctx: SbPickContext): string =>
  side === 'min'
    ? `before the earliest allowed date, ${formatLongDay(ctx.min as SbDateParts)}`
    : `after the latest allowed date, ${formatLongDay(ctx.max as SbDateParts)}`;

/**
 * A pick's text. A preview that repeats the label ("9:00 AM", "9:00 AM tomorrow") is the pick's
 * whole text, with no "9:00 AM: 9:00 AM".
 */
const view = (id: string, label: string, preview: string, reason: string, result: SbPickResult | null): SbPickView => {
  const repeats = !!preview && preview.startsWith(label);
  return { id, label: repeats ? preview : label, value: repeats ? '' : preview, reason, result: reason ? null : result };
};

/** One pick in a mode, with its preview and, outside min/max, the reason it is unavailable. */
export const computePick = (id: SbQuickPickId, mode: SbPickMode, ctx: SbPickContext, past = false): SbPickView => {
  const def = PICKS[id];
  const now = zoneNow(ctx);
  const today = now.clone().startOf('day');
  const todayParts = toDay(today);
  const computed = def.compute(now, today, ctx, past, mode !== 'time');
  // Read the instant back in the zone: a calendar add that lands in a spring-forward gap keeps a
  // stale offset, and the real wall time is the one after the gap.
  const picked = computed ? moment.tz(computed.valueOf(), now.tz() as string) : null;
  if (!picked) {
    if (id === 'latest' && isDevMode()) {
      console.error('sb quick pick "latest" needs a max');
    }
    return view(id, def.label, '', def.unavailable?.(ctx, past) ?? 'not available now', null);
  }
  const result: SbPickResult = {
    date: toDay(picked),
    time: mode !== 'date' && def.timed ? toTime(picked) : null,
  };
  if (mode !== 'date' && !result.time) {
    // A date pick in a time list: midnight would mislead, so keep the wall clock of now.
    result.time = toTime(now);
  }
  const side = mode === 'time' ? null : outOfBounds(result.date as SbDateParts, ctx.min, ctx.max);
  const preview = pickPreview(mode, result, todayParts);
  const hasOffset = mode === 'datetime' || (mode === 'time' && ctx.joinsDate);
  if (def.timed && hasOffset && at(picked, picked.hours(), picked.minutes()).valueOf() !== picked.valueOf()) {
    return view(id, def.label, preview, `the clocks go back then, so ${formatTimeDisplay(toTime(picked))} comes twice and would save as the first`, result);
  }
  return view(id, def.label, preview, side ? boundReason(side, ctx) : '', result);
};

/** One range preset, with its preview; disabled when either end falls outside min/max. */
export const computeRangePreset = (id: SbRangePresetId, ctx: SbPickContext): SbPickView => {
  const def = RANGES[id];
  const today = zoneNow(ctx).startOf('day');
  const todayParts = toDay(today);
  const [start, end] = def.compute(today);
  const result: SbPickResult = { date: start ? toDay(start) : null, time: null, end: end ? toDay(end) : null };
  const side = (result.date && outOfBounds(result.date, ctx.min, ctx.max)) || (result.end && outOfBounds(result.end, ctx.min, ctx.max)) || null;
  return view(id, def.label, rangePreview(result.date, result.end ?? null, todayParts), side ? boundReason(side, ctx) : '', result);
};

export const resolvePickIds = (mode: SbPickMode, set: SbQuickPickSet): readonly SbQuickPickId[] => {
  if (set === 'none') {
    return [];
  }
  return typeof set === 'string' ? QUICK_PICK_SETS[mode][set] : set;
};

export const resolveRangeIds = (set: SbRangePresetSet): readonly SbRangePresetId[] => {
  if (set === 'none') {
    return [];
  }
  return typeof set === 'string' ? RANGE_PRESET_SETS[set] : set;
};

export const computePicks = (mode: SbPickMode, set: SbQuickPickSet, ctx: SbPickContext): SbPickView[] =>
  resolvePickIds(mode, set).map((id) => computePick(id, mode, ctx, set === 'past'));

export const computeRangePresets = (set: SbRangePresetSet, ctx: SbPickContext): SbPickView[] =>
  resolveRangeIds(set).map((id) => computeRangePreset(id, ctx));

/** Today in a zone, for the calendar's today mark and the footer's Today. */
export const todayIn = (zone: string | null, nowMs = Date.now()): SbDateParts => toDay(zoneNow({ now: nowMs, zone, min: null, max: null }));

/** Now's wall clock in a zone. */
export const nowIn = (zone: string | null, nowMs = Date.now()): { date: SbDateParts; time: SbTimeParts } => {
  const now = zoneNow({ now: nowMs, zone, min: null, max: null });
  return { date: toDay(now), time: toTime(now) };
};
